<template>
    <div class="locale-flag">
        <img
            class="locale-flag__img"
            :alt="locale.territory.toUpperCase()"
            v-if="url"
            :src="url"
            @error="setFallbackUrl"
        />

        <div
            class="locale-flag__label"
            :class="{
                'locale-flag__label__short': size === 'short',
                'locale-flag__label__medium': size !== 'short' && size !== 'long',
                'locale-flag__label__long': size === 'long',
                'locale-flag__label__align-left': alignLeft,
             }"
            v-if="showLabel"
        >
            <div
                class="locale-flag__label__part locale-flag__label__part__short ot-text-tiny-strong"
                v-if="size === 'short'"
            >{{ locale.language.toLowerCase() }}-{{ locale.territory.toUpperCase() }}</div>
            <div
                class="locale-flag__label__part locale-flag__label__language"
                v-if="size !== 'short'"
            >{{ locale.languageDisplayName }}</div>
            <div
                class="locale-flag__label__part locale-flag__label__territory"
                v-if="size !== 'short'"
            >{{ locale.territoryDisplayName }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { GlobalizeLocaleInfo } from '@openticket/lib-localization';
import {
    computed, inject, ref, watch,
} from 'vue';
import type { Localization } from '../localization';

type Props = {
    locale: GlobalizeLocaleInfo,
    showLabel?: boolean,
    size?: 'long' | 'medium' | 'short',
    alignLeft?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    showLabel: true,
    size: 'medium',
    alignLeft: false,
});

const url = ref('');

const localization = inject<Localization>('localization');

const prefix = computed(() => {
    if (!localization || !localization.flagsUrlPrefix) {
        return '';
    }

    return localization.flagsUrlPrefix.replace(/\/*$/, '');
});

watch(() => localization && localization.initialized, () => {
    setFlagUrl();
});

watch(() => props.locale.flagUrl, () => {
    setFlagUrl();
}, { immediate: true });

function setFlagUrl(): void {
    if (!prefix.value) {
        url.value = '';

        return;
    }

    url.value = `${prefix.value}/${props.locale.flagUrl}`;
}

function setFallbackUrl(): void {
    if (!prefix.value) {
        url.value = '';

        return;
    }

    url.value = `${prefix.value}/flags/fallback.svg`;
}
</script>

<style scoped>
.locale-flag {
    display: flex;
    align-items: center;
    text-align: initial;
}

.locale-flag, .locale-flag * {
    box-sizing: border-box;
}

.locale-flag__img {
    height: 1.125rem;
}

.locale-flag__label {
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: flex-end;
    flex-grow: 1;

    font-size: .6875rem;
    line-height: .6875rem;
    vertical-align: top;
}

.locale-flag__label .locale-flag__label__territory {
    opacity: 0.5;

    font-size: .5625rem;
    line-height: .5625rem;
    vertical-align: bottom;
}

.locale-flag__label .locale-flag__label__part {
    position: relative;
    display: inline-block;

    padding-left: 0.75rem;
    text-align: right;
}

.locale-flag__label.locale-flag__label__align-left {
    align-items: flex-start;
}

.locale-flag__label.locale-flag__label__align-left .locale-flag__label__part {
    text-align: left;
}

.locale-flag__label.locale-flag__label__short {
    flex-direction: row;
}

.locale-flag__label.locale-flag__label__long {
    display: block;
}

.locale-flag__label.locale-flag__label__long .locale-flag__label__part {
    min-width: 50%;
}

@media (max-width: 25rem) {
    .locale-flag__label {
        display: flex;
    }

    .locale-flag__label.locale-flag__label__long {
        display: flex;
    }
}
</style>
